/**
 * API Interceptor
 *
 * Wraps global fetch to handle:
 * - 401 handling with automatic token refresh
 * - Immediate logout if refresh fails
 */

let originalFetch: typeof fetch = fetch
let isInitialized = false

/**
 * Initialize the global fetch interceptor
 */
export function initializeApiInterceptor() {
  if (isInitialized) return

  isInitialized = true
  originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url

    // Only intercept API calls to our backend
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
    if (!url.startsWith(API_URL)) {
      return originalFetch(input, init)
    }

    // Skip interception for auth endpoints (prevent loops)
    if (url.includes('/api/auth/refresh-token') ||
        url.includes('/api/auth/validate-token') ||
        url.includes('/api/auth/verify-otp') ||
        url.includes('/api/auth/request-otp')) {
      return originalFetch(input, init)
    }

    const response = await originalFetch(input, init)

    // Handle 401 responses
    if (response.status === 401) {
      const responseData = await response.clone().json().catch(() => ({}))

      console.warn('[apiInterceptor] 401 received:', responseData)

      // Get current token
      const token = localStorage.getItem('auth_token')

      // If token expired, try to refresh
      if (responseData.code === 'TOKEN_EXPIRED' && token) {
        console.log('[apiInterceptor] Attempting token refresh...')

        const refreshResponse = await originalFetch(`${API_URL}/api/auth/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          localStorage.setItem('auth_token', refreshData.token)
          if (refreshData.user) {
            localStorage.setItem('user_data', JSON.stringify(refreshData.user))
          }

          console.log('[apiInterceptor] Token refreshed, retrying original request')

          // Retry original request with new token
          const newInit = init || {}
          newInit.headers = {
            ...newInit.headers,
            'Authorization': `Bearer ${refreshData.token}`
          }
          return originalFetch(input, newInit)
        }
      }

      // Refresh failed or other 401 - trigger logout
      console.warn('[apiInterceptor] Authentication failed, triggering logout')

      // Call global logout handler
      if (window.__authLogout) {
        window.__authLogout()
      }

      // Redirect to login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }

    return response
  }
}

/**
 * Restore original fetch (for testing)
 */
export function restoreOriginalFetch() {
  window.fetch = originalFetch
  isInitialized = false
}
